<!-- Copyright (c) 2009-2021. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <cytomine-modal :active="active" :title="$t('button-add-image-from-upload')" @close="$emit('update:active', false)">
    <b-loading :is-full-page="false" :active="loading" class="small" />
    <template v-if="!loading">
      <template v-if="!processing">

        <div class="columns">
          <div class="column is-full">
            <div class="columns">
              <div class="column is-one-quarter has-text-right">
                <strong>{{$t('storage')}}</strong>
              </div>
              <div class="column is-half">
                <cytomine-multiselect v-model="selectedStorage" :options="storages" label="name" track-by="id" :allow-empty="false" />
              </div>
            </div>

            <div class="columns">
              <div class="column is-one-quarter has-text-right">
                <strong>{{$t('image')}}</strong>
              </div>
              <div class="column is-three-quarters">
                <b-field v-if="!selectedStorage || userCanWriteInStorage">
                  <b-upload v-model="selectedImage" type="is-link" drag-drop>
                    <section class="section">
                      <div class="content has-text-centered">
                        <template v-if="!selectedImage">
                          <p><i class="fas fa-upload fa-3x"></i></p>
                          <p>{{$t('choose-file-or-drop')}}</p>
                        </template>
                        <template v-else>
                          <p class="filename is-size-4"><i class="fas fa-file"></i> {{selectedImage.name}}</p>
                          <p class="has-text-grey is-size-6">{{$t('click-or-drop-new-file')}}</p>
                        </template>
                      </div>
                    </section>
                  </b-upload>
                </b-field>
                <b-field v-else>
                  <b-message type="is-info" has-icon icon-size="is-small" v-if="!userCanWriteInStorage">
                    {{ $t('info-cannot-upload-if-admin-not-in-storage') }}
                  </b-message>
                </b-field>
              </div>
            </div>

            <div class="columns">
              <div class="column is-one-quarter has-text-right">
                <strong>{{$t('name')}}</strong>
              </div>
              <div class="column is-half">
                <b-field>
                  <b-input v-model="name" :disabled="!selectedImage" name="name" v-validate="'required'" />
                </b-field>
              </div>
            </div>

            <div class="columns">
              <div class="column is-one-quarter has-text-right">
                <strong>{{$t('tags')}}</strong>
              </div>
              <div class="column is-half">
                <cytomine-multiselect
                  v-model="selectedTags"
                  :multiple="true"
                  :options="tags"
                  label="name" track-by="id"
                />

              </div>
            </div>
          </div>

        </div>

        <div class="columns">
          <div class="column is-narrow has-text-right" style="width: 12.5%;">
            <strong>{{$t('description')}}</strong>
          </div>
          <div class="column is-10">
            <div class="keyword-info">
              <i class="fas fa-info-circle"></i>
              <i18n path="info-keyword-stop-preview-description">
                <span place="keyword" class="keyword"> {{ stopPreviewKeyword }} </span>
              </i18n>
            </div>
            <cytomine-quill-editor v-model="descriptionContent" :placeholder="$t('enter-description')" />
          </div>
        </div>
      </template>
      <b-loading v-else :is-full-page="false" :active="processing && !success && !failed" class="small" />

      <article v-if="success" class="message is-success">
        <section class="message-body">
          <div class="media">
            <div class="media-content">
              This image {{name}} has been successfully uploaded to the project.
            </div>
          </div>
        </section>
      </article>

      <article v-if="failed" class="message is-danger">
        <section class="message-body">
          <div class="media">
            <div class="media-content">
              An error occured.
            </div>
          </div>
        </section>
      </article>

      <div class="columns" v-if="!success && !failed">
        <div class="column is-half flex-column is-offset-one-quarter">
          <progress v-if="ongoingUpload" class="progress is-success" :value="imageToUpload.progress" max="100">
            {{imageToUpload.progress}}%
          </progress>

        </div>
      </div>
    </template>
    <template #footer>
      <button class="button" type="button" @click="$emit('update:active', false)">
        {{$t('button-cancel')}}
      </button>
      <button class="button is-success" @click="startUpload(imageToUpload)" :disabled="!uploadable || success">
        {{$t('start-upload')}}
      </button>
    </template>
  </cytomine-modal>
</template>

<script>
import constants from '@/utils/constants.js';
import {get} from '@/utils/store-helpers';
import {Cytomine, AbstractImage, AttachedFile, Configuration, Description, ImageInstance, StorageAccessCollection, TagCollection, TagDomainAssociation, UploadedFile, StorageUserCollection} from 'cytomine-client';
import axios from 'axios';
import CytomineMultiselect from '@/components/form/CytomineMultiselect';
import CytomineQuillEditor from '@/components/form/CytomineQuillEditor';
import CytomineModal from '@/components/utils/CytomineModal';
import CytomineTable from '@/components/utils/CytomineTable';

export default {
  name: 'add-image-from-upload',
  props: {
    active: Boolean,
  },
  components: {
    CytomineTable,
    CytomineModal,
    CytomineMultiselect,
    CytomineQuillEditor
  },
  data() {
    return {
      loading: true,
      storages:[],
      selectedStorage:null,
      selectedStorageUsers: [],
      descriptionContent: '',
      selectedProtocol:null,
      selectedImage:null,
      imageToUpload:null,
      name: null,
      ongoingUpload:false,
      tags: [],
      selectedTags: [],
      signature: '',
      signatureDate: '',
      processing: false,
      success : false,
      failed : false,
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    project: get('currentProject/project'),
    canManageProject() {
      return this.$store.getters['currentProject/canManageProject'];
    },
    descriptionWithoutKeywords() {
      return this.description.data.replace(new RegExp(constants.STOP_PREVIEW_KEYWORD, 'g'), '');
    },
    stopPreviewKeyword() {
      return constants.STOP_PREVIEW_KEYWORD;
    },
    uri() {
      return '/upload';
    },
    queryString() {
      return new URLSearchParams({
        idStorage: (this.selectedStorage) ? this.selectedStorage.id : null,
        sync: true
      }).toString();
    },
    uploadable(){
      return this.selectedStorage != null && this.selectedImage != null && this.userCanWriteInStorage;
    },
    userCanWriteInStorage() {
      console.log(this.selectedStorageUsers===undefined);
      console.log(this.selectedStorageUsers);
      return this.selectedStorageUsers===undefined ? false : this.selectedStorageUsers.findIndex(u => u.id === this.currentUser.id && (u.role === 'ADMINISTRATION' || u.role === 'WRITE'))!==-1;
    },
  },
  watch: {
    active(val) {
      if(val) {
        this.selectedStorage = null;
        this.descriptionContent = '';
        this.selectedImage = null;
        this.imageToUpload = null;
        this.name = null;
        this.ongoingUpload = false;
        this.processing = false;
        this.success = false;
        this.failed = false;
      }
    },
    async queryString() {
      this.generateSignature();
    },
    selectedImage(file) {
      if(file) {
        this.name = file.name;
        this.imageToUpload = {
          file,
          uploading: false,
          progress: 0,
          uploadedFile: null, // null if upload not finished, false if upload failed, UploadedFile instance if upload successful
          abstractImage: null, // null if upload not finished, false if upload failed, AbstractImage instance if upload successful
          cancelToken: null
        };
      }
    },
    async selectedStorage(storage) {
      if (storage) {
        this.selectedStorageUsers = (await StorageUserCollection.fetchAll({filterKey: 'storage', filterValue: this.selectedStorage.id})).array;
      }
    }
  },
  methods: {
    async startUpload(fileWrapper) {
      if(fileWrapper.uploading || fileWrapper.uploadedFile !== null) {
        return;
      }

      let formData = new FormData();
      formData.append('files[]', fileWrapper.file);
      fileWrapper.cancelToken = axios.CancelToken.source();
      fileWrapper.uploading = true;
      this.ongoingUpload = true;
      this.processing = true;
      axios.post(
        this.uri + '?' + this.queryString,
        formData,
        {
          headers: {
            'authorization': `CYTOMINE ${this.currentUser.publicKey}:${this.signature}`,
            'dateFull': this.signatureDate, // will replace actual date value, so that signature is valid
            'content-type-full': 'null' // will erase actual content-type value, so that signature is valid
          },
          onUploadProgress: progress => {
            fileWrapper.progress = Math.floor((progress.loaded * 100) / progress.total);
          },
          cancelToken: fileWrapper.cancelToken.token
        }
      ).then(async response => {
        fileWrapper.uploadedFile = new UploadedFile(response.data[0].uploadedFile);
        fileWrapper.abstractImage = new AbstractImage(response.data[0].images[0].image);

        await this.rename();
        await this.associateMetadata();
        await this.addToProject();
      }).catch(error => {
        this.processing = false;
        if(!axios.isCancel(error)) {
          console.log(error);
          fileWrapper.uploadedFile = false;
          this.failed = true;
          this.errorMessage = error;
        }
      }).finally(() => fileWrapper.uploading = false);
    },

    async rename(){
      try {

        if(this.name && this.imageToUpload.abstractImage.originalFilename){
          this.imageToUpload.abstractImage.originalFilename = this.name;
          this.imageToUpload.abstractImage = await this.imageToUpload.abstractImage.save();
        }
      }
      catch(error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-image-rename')});
        this.failed = true;
        this.errorMessage = this.$t('notif-error-image-rename');
      }
    },

    async associateMetadata(){
      try {
        let associationPromises = [];
        for(let i = 0; i < this.selectedTags.length; i++) {
          associationPromises.push(new TagDomainAssociation({tag : this.selectedTags[i].id}, this.imageToUpload.abstractImage).save());
        }
        await Promise.all(associationPromises);
      }
      catch(error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-save-prop')});
        this.failed = true;
        this.errorMessage = this.$t('notif-error-save-prop');
      }
      //description
      if(this.descriptionContent){
        try {
          await new Description({data: this.descriptionContent, object: this.imageToUpload.abstractImage}).save();
        }
        catch(error) {
          console.log(error);
          this.$notify({type: 'error', text: this.$t('notif-error-update-description')});
          this.failed = true;
          this.errorMessage = this.$t('notif-error-update-description');
        }
      }
    },
    async addToProject(){
      let propsTranslation = {imageName: this.imageToUpload.abstractImage.originalFilename, projectName: this.project.name};
      try {
        let image = await new ImageInstance({baseImage: this.imageToUpload.abstractImage.id, project: this.project.id}).save();
        this.$emit('addImage', image);
        this.$notify({
          type: 'success',
          text: this.$t('notif-success-add-image', propsTranslation)
        });

        let updatedProject = this.project.clone();
        updatedProject.numberOfImages++;
        this.$store.dispatch('currentProject/updateProject', updatedProject);
        this.success = true;
      }
      catch(error) {
        console.log(error);
        this.$notify({
          type: 'error',
          text: this.$t('notif-error-add-image', propsTranslation)
        });
      }

    },
    async fetchStorages() {
      try {
        console.log((await StorageAccessCollection.fetchAll()).array);
        this.storages = (await StorageAccessCollection.fetchAll()).array.filter(storage => storage.permission == 'ADMINISTRATION' || storage.permission == 'WRITE');
        //this.selectedStorage = this.storages.find(storage => storage.user === this.currentUser.id);
      }
      catch(error) {
        console.log(error);
        this.newUploadError = true;
      }
    },
    async fetchTags() {
      try {
        this.tags = (await TagCollection.fetchAll()).array.sort((a, b) => a.name.localeCompare(b.name));
      }
      catch(error) {
        console.log(error);
      }
    },
    async generateSignature() {
      this.signatureDate = new Date().toISOString();
      try {
        this.signature = await Cytomine.instance.fetchSignature({
          uri: this.uri,
          queryString: this.queryString,
          method: 'POST',
          date: this.signatureDate
        });
      }
      catch(error) {
        this.newUploadError = true;
      }
    },
  },
  async created() {
    console.log('form.created');
    this.loading = false;
    this.fetchStorages();
    this.fetchTags();
    this.generateSignature();
  }
};
</script>

<style scoped>
>>> .animation-content {
  max-width: 60% !important;
  width: 60%;
}

>>> .modal-card {
  width: 100%;
  height: 80vh;
}

.image-overview {
  max-height: 4rem;
  max-width: 10rem;
}
</style>
